export const ONBOARDING_GUIDE = `
# Onboarding Study-Setup Guide

Welcome to Dioscuro Academy. This guide walks you through everything you need to
set up before you start your first module. Get this done once — then focus on
learning.

## Prerequisites

You need three things before you begin:

- **A computer you can install software on.** A personal laptop or desktop running
  Linux, macOS, or Windows with WSL2 works fine. A locked-down corporate machine
  will not — you need root/admin access to install tools and run containers.
- **A GitHub account.** Your labs live in Git repositories. If you do not have an
  account yet, sign up at the official site. Use a personal address you control,
  not a work one that may disappear.
- **Basic terminal comfort.** You should be able to open a terminal, navigate
  directories with \`cd\`, and run commands without freezing up. You do not need to
  be a shell expert — you will become one during the programme.

If any of these is a blocker, fix it before the next step. Do not skip ahead.

## Install Your Toolkit

These are the five tools you will use across nearly every lab. Install them from
their official sites before your first check-in.

- **\`git\`** — version control. Every lab is a Git repository; you will clone,
  branch, commit, and push throughout the curriculum. Install from the official
  Git site.
- **Docker Desktop** — runs containers locally. Containerisation is foundational
  to DevOps work; you will use Docker in labs from week one. Install from the
  official Docker site. On Linux you can install Docker Engine directly instead.
- **\`kubectl\`** — the Kubernetes command-line client. Even before you manage your
  own cluster, you will use \`kubectl\` to inspect, deploy, and debug workloads.
  Install from the official Kubernetes site.
- **\`terraform\`** — infrastructure as code. You will provision cloud resources
  declaratively from the first IaC module onward. Install from the official
  HashiCorp site.
- **A code editor — VS Code recommended.** You need something with syntax
  highlighting, integrated terminal, and Git support. VS Code is free, has
  excellent extensions for YAML, HCL, and Dockerfile formats, and is what most
  Dioscuro engineers use. Install from the official Microsoft site. If you prefer
  another editor you already know well, keep using it.

After installing, verify each tool from a fresh terminal session:

\`\`\`
git --version
docker --version
kubectl version --client
terraform --version
code --version
\`\`\`

If any command is not found, re-check the installation instructions for your OS.

## Get the Labs

The labs monorepo contains the starter code, solution branches, and local
run scripts for every hands-on challenge. Clone it once to your machine:

\`\`\`
git clone <your-labs-repo-url>
cd <repo-name>
\`\`\`

Your mentor or programme coordinator will share the real URL during onboarding.
Use the placeholder above as written until then.

To run a lab locally, enter the lab's directory and follow the \`README.md\` in
that folder. Most labs include a \`docker compose up\` or a \`make start\` command
that brings up the environment. If something does not start, check \`docker ps\`
and read the error output before asking for help — you will almost always find
the answer there.

## How to Study

The recommended study loop is: Academy modules first, then the corresponding
hands-on lab, then verify against the career-path checkpoint.

1. **Watch the lesson.** Each lesson in the Academy has a sprite-teacher video —
   a cel-cartoon instructor who walks through the concept beat by beat. Watch it
   at 1x speed the first time; you can re-watch at 1.5x once you have the shape
   of the idea. Take notes.
2. **Do the lab.** Open the lab, read the brief fully before touching anything,
   then work through it. The labs are calibrated so a focused engineer can finish
   a medium one in about an hour. Do not paste solutions from the internet; the
   point is the failure-and-fix cycle, not the output.
3. **Check the career-path checkpoint.** After each module group, the career-path
   page shows the skills that section covers. Mark them honestly — you will use
   this self-assessment in your bi-weekly check-in, and inflating it only hurts
   you.
4. **Keep a daily rhythm.** Thirty focused minutes every day beats a six-hour
   weekend binge. The streak counter on your profile rewards consistency; use it
   as a habit anchor, not a score to game.

Show up to the **bi-weekly mentor check-in** prepared. Come with one thing that
clicked, one thing that is still murky, and your latest career-path checkpoint
self-assessment. The check-in is your main feedback loop — make it count.

## Getting Help

- **Your mentor** is your first port of call for anything substantive — blocked on
  a concept, unsure which direction to take, getting odd errors you cannot explain
  after 20 minutes of independent investigation.
- **The AI mentor** on your profile page handles quick questions. It is capped at
  10 questions per day so you focus them. Ask specific, scoped questions: not
  "explain Kubernetes" but "why does my Pod stay in \`Pending\` when I have
  capacity on the node?"
- **Slack** is where the team lives day-to-day. Post in the relevant channel,
  share your error output, describe what you already tried. A focused question
  gets a fast answer; a vague one gets a request for clarification.

When you are stuck: read the error message carefully, check the tool's official
docs, search the monorepo for similar usage, try one thing at a time. Then ask.
`;
